import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import PageSEO from '../components/PageSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: MapPin,
    title: 'Adresse',
    content: 'Mairie de Gandiaye\nRoute Nationale N°1\nGandiaye, Région de Kaolack, Sénégal',
  },
  {
    icon: Phone,
    title: 'Téléphone',
    content: '+221 33 949 10 10',
    link: 'tel:+221339491010',
  },
  {
    icon: Mail,
    title: 'Email',
    content: 'mairie.gandiaye@gmail.com',
    link: 'mailto:mairie.gandiaye@gmail.com',
  },
  {
    icon: Clock,
    title: 'Horaires',
    content: 'Lundi - Vendredi\n8h00 - 13h30 / 14h30 - 17h00',
  },
];

const departments = [
  { value: 'general', label: 'Renseignements généraux' },
  { value: 'etat_civil', label: 'État Civil' },
  { value: 'urbanisme', label: 'Urbanisme' },
  { value: 'social', label: 'Affaires Sociales' },
  { value: 'technique', label: 'Services Techniques' },
  { value: 'maire', label: 'Cabinet du Maire' },
];

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    department: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Votre message a été envoyé avec succès !');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <main className="pt-14 md:pt-16" data-testid="contact-page">
      <PageSEO
        title="Contact"
        description="Contactez la mairie de Gandiaye. Adresse, téléphone, email et formulaire de contact."
        path="/contact"
      />
      {/* Hero */}
      <section className="bg-emerald-600 py-16 md:py-24">
        <div className="container-main">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
            Contact
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Contactez-nous pour toute question ou demande d'information. 
            Notre équipe est à votre écoute.
          </p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 bg-white">
        <div className="container-main">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((item, index) => (
              <Card key={index} className="card-teranga text-center">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <item.icon className="text-emerald-600" size={24} />
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">{item.title}</h3>
                  {item.link ? (
                    <a 
                      href={item.link} 
                      className="text-neutral-600 hover:text-emerald-600 whitespace-pre-line"
                    >
                      {item.content}
                    </a>
                  ) : (
                    <p className="text-neutral-600 whitespace-pre-line">{item.content}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-heading font-bold text-neutral-800 mb-6">
                Envoyez-nous un message
              </h2>
              
              {isSubmitted ? (
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                      Message envoyé !
                    </h3>
                    <p className="text-neutral-600 mb-6">
                      Merci de nous avoir contactés. Nous vous répondrons dans les plus brefs délais.
                    </p>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setIsSubmitted(false);
                        setFormData({
                          name: '',
                          email: '',
                          phone: '',
                          subject: '',
                          message: '',
                          department: '',
                        });
                      }}
                    >
                      Envoyer un autre message
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Nom complet *
                      </label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Votre nom"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="votre@email.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Téléphone
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+221 XX XXX XX XX"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Service concerné *
                      </label>
                      <Select
                        value={formData.department}
                        onValueChange={(value) => setFormData({ ...formData, department: value })}
                      >
                        <SelectTrigger data-testid="select-department">
                          <SelectValue placeholder="Sélectionner un service" />
                        </SelectTrigger>
                        <SelectContent>
                          {departments.map((dept) => (
                            <SelectItem key={dept.value} value={dept.value}>
                              {dept.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Sujet *
                    </label>
                    <Input
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="Objet de votre message"
                      data-testid="input-subject"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="Votre message..."
                      data-testid="input-message"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6"
                    disabled={isSubmitting}
                    data-testid="submit-contact-btn"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Envoi en cours...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send size={18} />
                        Envoyer le message
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Map */}
            <div>
              <h2 className="text-2xl font-heading font-bold text-neutral-800 mb-6">
                Nous trouver
              </h2>
              <div className="map-container h-[400px] lg:h-full min-h-[400px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15429.506792843866!2d-15.95!3d14.25!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xec17e0000000001%3A0x1!2sGandiaye%2C%20S%C3%A9n%C3%A9gal!5e0!3m2!1sfr!2sfr!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localisation de Gandiaye"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Numbers */}
      <section className="py-12 bg-red-600">
        <div className="container-main">
          <h2 className="text-2xl font-heading font-bold text-white text-center mb-8">
            Numéros d'Urgence
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-white/10 border-none text-white text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-bold mb-2">18</div>
                <div className="text-white/80">Sapeurs-Pompiers</div>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border-none text-white text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-bold mb-2">800 00 20 20</div>
                <div className="text-white/80">Gendarmerie</div>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border-none text-white text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-bold mb-2">15</div>
                <div className="text-white/80">Urgences Santé</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
